/**
 * **Add equipment** — `docs/visit-page.md` §7.9 (Board 5's button): a popup
 * with *Type*, the days (chips) and *Hours per day* → a `work_item_equipment`
 * row with its days, never a plan line (§2.5: the approved quote is not
 * edited from here).
 *
 * No board draws the popup itself (a DEVIATION REQUEST in the slice's
 * report): it is the visit page's popup (`VisitPopup`), Board 18 H's shell and
 * tile, with the Rent sheet's day chips.
 */
import { useState } from 'react'
import type { AddEquipmentOptions, EquipmentRead, VisitRead } from '@rowted/shared'
import { PickField, SheetField, TextBox } from './equipment-fields.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { COMMON, EQUIPMENT, RENT } from './copy.js'
import { dayText } from './format.js'
import { DayChips } from './day-chips.js'
import { VisitButton, VisitPopup, VisitTile } from './sheet.js'

export function AddEquipmentPopup({ visitId, read, phone, onClose, onWritten }: {
  visitId: string
  read: VisitRead
  phone: boolean
  onClose: () => void
  onWritten: (next: EquipmentRead) => void
}) {
  const { data } = useVisitTab<AddEquipmentOptions>(visitId, '/equipment/options')
  const write = useVisitWrite(visitId)
  const [typeId, setTypeId] = useState<string | null>(null)
  const [days, setDays] = useState<number[]>([])
  const [hours, setHours] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const perDay = Number(hours.trim())
  const ready = Boolean(typeId) && days.length > 0 && hours.trim() !== '' && Number.isFinite(perDay) && perDay > 0 && !busy

  async function add() {
    if (!ready || !typeId) return
    setBusy(true)
    setError(null)
    try {
      const result = await write('POST', '/equipment', {
        typeId,
        days: days.map((day) => ({ day, hours: perDay })),
      }, { tab: 'equipment' })
      if (result.tab) onWritten(result.tab as EquipmentRead)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  return (
    <VisitPopup
      width={420}
      phone={phone}
      title={EQUIPMENT.add}
      sub={read.reference}
      mark={<VisitTile glyph="loader" tone="indigo" size={34} />}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" disabled={!ready} onClick={() => void add()}>{EQUIPMENT.add}</VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <SheetField label={COMMON.type} required>
          {({ id, describedBy }) => (
            <PickField id={id} describedBy={describedBy} label={COMMON.type} value={typeId} onChange={setTypeId} phone={phone}
              options={(data?.types ?? []).map((type) => ({ value: type.id, label: type.name }))} />
          )}
        </SheetField>
        <SheetField label={COMMON.days} required group>
          {({ labelId }) => (
            <DayChips
              labelledBy={labelId}
              phone={phone}
              days={(data?.days ?? []).map((day) => ({ index: day.index, label: RENT.day(String(day.index), dayText(day.date, 'day')) }))}
              value={days}
              onChange={setDays}
            />
          )}
        </SheetField>
        <SheetField label={EQUIPMENT.hoursPerDay} required>
          {({ id, describedBy }) => (
            <TextBox id={id} describedBy={describedBy} phone={phone} inputMode="decimal" value={hours} onChange={setHours} />
          )}
        </SheetField>
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}
